import { useSafeNavigate } from '@/services/router/safe-navigate';
import { activeRouteStore } from '@customerio/hydra-nav-state';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useStore } from '@nanostores/react';
import clsx from 'clsx';
import { useCallback, useMemo, useEffect, useState } from 'react';
import { useActiveBundle } from '../../../../context/active-bundle';
import { useCurrentWorkspace } from '../../../../context/current-workspace';
import { AppBundle } from '../../../../utils/app-bundles';
import '../main-navigation/main-nav.css';
import type { NavItem as NavItemType, NavItemGroup } from '../main-navigation/routes';
import { generateRoutes, isNavItemActive } from '../main-navigation/routes';
import { isEmptyString } from '@/utils/string';

import { shouldProcessLinkClick, useIsSettings } from '../../../../utils/navigation';

import useSetupGuideExperiment from '@/features/experiments/setup-guide/hook';
import { useAnalytics } from '@/services/analytics';
import { useListWorkspaceObjectTypes } from '@/features/workspaces/hooks';
import { useAuth } from '@/services/auth';
import { AccountState } from '@/services/auth/types';
import { useSampleWorkspace } from '@/features/workspaces/sample-workspace';
import {
  SideNavGroup,
  NavItem,
  NavItemExpandable,
  NavItemExpandableTrigger,
  NavItemExpandableContent,
  Box,
  Label,
  InlineStack,
  useSideNavState,
} from '@customerio/pluma-components/react';

import * as styles from './index.css';

function ExtendedNavItem(props: {
  item: NavItemType;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>, item: NavItemType) => unknown;
}) {
  const { item, onClick } = props;

  const currentWorkspace = useCurrentWorkspace();
  const { isSampleWorkspace } = useSampleWorkspace();
  const sideNavState = useSideNavState();
  const activeRoute = useStore(activeRouteStore);

  let itemProps = { ...item };
  /**
   * Allow prop override when on the sample workspace
   * Override only occurs when alternate props are available, otherwise the default apply
   */
  if (isSampleWorkspace(currentWorkspace?.id) && item.onSampleWorkspace != null) {
    itemProps = { ...itemProps, ...item.onSampleWorkspace };
  }
  const {
    iconName,
    href,
    label,
    id,
    showPremiumLabel = false,
    showNewLabel = false,
    variant,
    showIconOnExpand,
    children,
  } = itemProps;

  const classNames = [];

  if (variant === 'verdant') {
    classNames.push(styles.navLinkVerdant);
  } else if (variant === 'evergreen') {
    classNames.push(styles.navLinkEvergreen);
  }

  const className = clsx(classNames);

  const hideIcon = iconName == null || (showIconOnExpand === false && sideNavState?.isCollapsed === false);

  if (children != null) {
    return (
      <NavItemExpandable key={item.id}>
        <NavItemExpandableTrigger icon={hideIcon ? undefined : iconName}>{label}</NavItemExpandableTrigger>

        <NavItemExpandableContent>
          {children.map((child) => {
            return <ExtendedNavItem key={child.id} item={child} onClick={onClick} />;
          })}
        </NavItemExpandableContent>
      </NavItemExpandable>
    );
  }

  return (
    <NavItem
      className={className}
      data-test-nav-item={id}
      href={href}
      icon={hideIcon ? undefined : iconName}
      isSelected={isNavItemActive(item, activeRoute)}
      onClick={(e) => onClick(e as any, item)}
    >
      <InlineStack gap="050" alignBlock="center">
        <Box as="span" truncate={true}>
          {label}
        </Box>
        {showNewLabel ? (
          <Label color="purple" flexShrink="0">
            New
          </Label>
        ) : null}
        {showPremiumLabel ? (
          <Label color="plum" flexShrink="0">
            Premium
          </Label>
        ) : null}
      </InlineStack>
    </NavItem>
  );
}

export function MainNavigationNew() {
  const currentWorkspace = useCurrentWorkspace();
  const permissions = usePermissions();
  const auth = useAuth();
  const safeNavigate = useSafeNavigate();
  const activeApp = useActiveBundle();
  const { isSampleWorkspace } = useSampleWorkspace();
  const { track } = useAnalytics();
  const journeysObjectTypes = useListWorkspaceObjectTypes(currentWorkspace?.id);
  const isTrial = auth.account.account_state === AccountState.Free;

  // setup guide nav setup
  const [isSetupGuideComplete, setIsSetupGuideComplete] = useState(!isTrial);
  const { sampleWorkspaceId } = useSampleWorkspace();

  const { isLoading: isSetupGuideLoading, setupGuide } = useSetupGuideExperiment();
  const { isSettingsPage } = useIsSettings();

  useEffect(() => {
    if (isSetupGuideLoading) return;
    if (activeApp === AppBundle.Journeys && setupGuide.completed_at == null) {
      setIsSetupGuideComplete(false);
      return;
    }
    if (setupGuide.completed_at != null && !isSetupGuideComplete) {
      setIsSetupGuideComplete(true);
    }
  }, [currentWorkspace?.id, activeApp, isSetupGuideComplete, isSetupGuideLoading, setupGuide?.completed_at]);

  const onClickNavItem = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, item: NavItemType) => {
      if (!shouldProcessLinkClick(event)) return;

      if (item.id === 'invite-teammates') {
        void track('mainNav', 'teamInviteClicked', {
          isSampleWorkspace: isSampleWorkspace(currentWorkspace?.id),
        });
      }
      if (item.id === 'view-workspace') {
        void track('mainNav', 'viewWorkspaceClicked', {
          isSampleWorkspace: isSampleWorkspace(currentWorkspace?.id),
        });
        /**
         * We return early here so that we do not safeNavigate, and instead hard refresh
         * this is because permissions can get out of sync otherwise
         * */
        return;
      }

      event.preventDefault();
      if (item.href == null) return;

      safeNavigate(item.href, item.transitionOptions ?? {});
    },
    [isSampleWorkspace, currentWorkspace?.id, safeNavigate, track],
  );

  // generating navigation items and groups
  const navItemGroups = useMemo(() => {
    let items: NavItemGroup[] = [];

    if (currentWorkspace != null) {
      const objectTypes = journeysObjectTypes.data ?? [];
      items = generateRoutes(currentWorkspace, auth, permissions, {
        sampleWorkspaceId,
        shouldShowHome: !isSetupGuideComplete && !isSampleWorkspace(currentWorkspace.id),
        objectTypes,
        isNewLayout: true,
      });
    }

    if (
      isSettingsPage &&
      auth.user?.last_cio_workspace_id != null &&
      !isEmptyString(auth.user?.last_cio_workspace_id) &&
      currentWorkspace != null
    ) {
      items = generateRoutes(currentWorkspace, auth, permissions, { isNewLayout: true });
    }

    const groups = items.map((group, i) => {
      let isTailGroup = false;

      const navItems = group.items.map((item) => {
        if (item.id === 'invite-teammates' || item.id === 'view-workspace') {
          isTailGroup = true;
        }

        return <ExtendedNavItem key={item.id} item={item} onClick={onClickNavItem} />;
      });

      return (
        <SideNavGroup
          key={`group_${i}`}
          title={group.title}
          mt={isTailGroup ? 'auto' : undefined}
          gap={isTailGroup ? '100' : undefined}
        >
          {navItems}
        </SideNavGroup>
      );
    });

    return groups;
  }, [
    auth,
    isSampleWorkspace,
    sampleWorkspaceId,
    currentWorkspace,
    permissions,
    isSettingsPage,
    isSetupGuideComplete,
    journeysObjectTypes.data,
    onClickNavItem,
  ]);

  return <>{navItemGroups}</>;
}
